// redux/accountSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const ACCOUNT_URL = `${process.env.REACT_APP_API_BASE_URL}/api/account/`;
const ME_URL = `${process.env.REACT_APP_API_BASE_URL}/api/users/me/`;
const RESET_PASSWORD_URL = `${process.env.REACT_APP_API_BASE_URL}/api/users/reset-password/`;
const USERS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/users/users/`;

// ✅ NEW: Dashboard API URLs
const DASHBOARD_SUMMARY_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/dashboard/summary/`;
const QUICK_STATS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/dashboard/quick-stats/`;

// ✅ NEW: Optional dashboard card endpoints (fetched lazily, only when a user enables the card)
export const OPTIONAL_CARD_ENDPOINTS = {
  employee_dashboard_stats: `${process.env.REACT_APP_API_BASE_URL}/api/employees/dashboard-stats/`,
  payment_dashboard_stats: `${process.env.REACT_APP_API_BASE_URL}/api/payments/dashboard-stats/`,
  daily_payments_today: `${process.env.REACT_APP_API_BASE_URL}/api/payments/dashboard-stats/daily/`,
  daily_receipts_today: `${process.env.REACT_APP_API_BASE_URL}/api/payments/dashboard-stats/daily/`,
  today_absences: `${process.env.REACT_APP_API_BASE_URL}/api/employees/dashboard-stats/today-absences/`,
  events_summary: `${process.env.REACT_APP_API_BASE_URL}/api/events/events/summary/`,
  total_inventory_value: `${process.env.REACT_APP_API_BASE_URL}/api/inventory/store-inventory/total-value/`,
};

// ✅ NEW: Finance Staff dashboard section (date-range filtered)
const FINANCE_RANGE_STATS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/payments/dashboard-stats/range/`;

// Existing thunks...
export const fetchAccountInfo = createAsyncThunk('account/fetch', async (_, thunkAPI) => {
  try {
    const res = await axios.get(ACCOUNT_URL);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data || 'Fetch failed');
  }
});

export const resetPassword = createAsyncThunk(
  'account/resetPassword',
  async ({ newPassword }, thunkAPI) => {
    try {
      const res = await axios.post(
        RESET_PASSWORD_URL,
        { new_password: newPassword }
      );
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'فشل إعادة تعيين كلمة المرور');
    }
  }
);

export const updateAccountInfo = createAsyncThunk(
  'account/update',
  async (formData, thunkAPI) => {
    try {
      const res = await axios.put(ACCOUNT_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Update failed');
    }
  }
);

export const fetchUserInfo = createAsyncThunk('account/fetchUser', async (_, thunkAPI) => {
  try {
    const res = await axios.get(ME_URL);
    localStorage.setItem('role', res.data.role);
    localStorage.setItem('user', JSON.stringify(res.data));
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data || 'Fetch user info failed');
  }
});

export const fetchAccountUsers = createAsyncThunk('account/fetchUsers', async (_, thunkAPI) => {
  try {
    const res = await axios.get(USERS_URL);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data || 'Fetch users failed');
  }
});

// ✅ NEW: Dashboard Summary API
export const fetchDashboardSummary = createAsyncThunk(
  'account/fetchDashboardSummary',
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(DASHBOARD_SUMMARY_URL);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.error || 'فشل في جلب بيانات لوحة التحكم'
      );
    }
  }
);

// ✅ NEW: Quick Stats API (for real-time updates)
export const fetchQuickStats = createAsyncThunk(
  'account/fetchQuickStats',
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(QUICK_STATS_URL);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.error || 'فشل في جلب الإحصائيات السريعة'
      );
    }
  }
);

// ✅ NEW: Refresh dashboard data with optional force flag
export const refreshDashboardData = createAsyncThunk(
  'account/refreshDashboardData',
  async ({ forceRefresh = false } = {}, thunkAPI) => {
    const state = thunkAPI.getState();
    const lastUpdate = state.account.dashboardLastUpdate;
    const now = Date.now();
    
    // If not forcing refresh and data is less than 5 minutes old, skip
    if (!forceRefresh && lastUpdate && (now - lastUpdate) < 5 * 60 * 1000) {
      return thunkAPI.rejectWithValue('Data is still fresh');
    }
    
    try {
      // Fetch both summary and quick stats in parallel
      const [summaryRes, quickStatsRes] = await Promise.all([
        axios.get(DASHBOARD_SUMMARY_URL),
        axios.get(QUICK_STATS_URL)
      ]);
      
      return {
        summary: summaryRes.data,
        quickStats: quickStatsRes.data,
        timestamp: now
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.error || 'فشل في تحديث بيانات لوحة التحكم'
      );
    }
  }
);

// ✅ NEW: Generic lazy fetch for an optional dashboard card's data.
// Several card ids share one endpoint (e.g. daily_payments_today / daily_receipts_today both
// read /payments/dashboard-stats/daily/), so results are cached by endpoint URL, not card id,
// to avoid firing the same request twice.
export const fetchOptionalCardData = createAsyncThunk(
  'account/fetchOptionalCardData',
  async (cardId, thunkAPI) => {
    const url = OPTIONAL_CARD_ENDPOINTS[cardId];
    if (!url) {
      return thunkAPI.rejectWithValue(`Unknown dashboard card: ${cardId}`);
    }
    const state = thunkAPI.getState();
    if (state.account.optionalCardsData[url]) {
      return thunkAPI.rejectWithValue('already-cached');
    }
    try {
      const res = await axios.get(url);
      return { url, data: res.data };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.error || 'فشل في جلب بيانات البطاقة');
    }
  }
);

// ✅ NEW: Fetch stats for the Finance Staff dashboard section for a given date range.
// Unlike fetchOptionalCardData, this always refetches (no cache-by-url skip) since the
// caller explicitly re-triggers it whenever the user applies a new date range.
export const fetchFinanceRangeStats = createAsyncThunk(
  'account/fetchFinanceRangeStats',
  async ({ startDate, endDate }, thunkAPI) => {
    try {
      const res = await axios.get(FINANCE_RANGE_STATS_URL, {
        params: { start_date: startDate, end_date: endDate }
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.error || 'فشل في جلب إحصائيات المالية حسب الفترة');
    }
  }
);

// ✅ NEW: Save the current user's enabled optional dashboard cards
export const saveDashboardCardPrefs = createAsyncThunk(
  'account/saveDashboardCardPrefs',
  async (cardIds, thunkAPI) => {
    try {
      const res = await axios.patch(ME_URL, { dashboard_card_prefs: cardIds });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.error || 'فشل حفظ إعدادات البطاقات');
    }
  }
);

const accountSlice = createSlice({
  name: 'account',
  initialState: {
    data: null,
    user: null,
    users: [],
    
    // ✅ NEW: Dashboard state
    dashboardSummary: null,
    quickStats: null,
    dashboardLoading: false,
    dashboardError: null,
    dashboardLastUpdate: null,

    // ✅ NEW: Optional dashboard card data, cached by endpoint URL (see OPTIONAL_CARD_ENDPOINTS)
    optionalCardsData: {},
    optionalCardsLoading: {},

    // ✅ NEW: Finance Staff dashboard section (date-range filtered)
    financeRangeStats: null,
    financeRangeLoading: false,
    financeRangeError: null,

    // General state
    loading: false,
    error: null,
  },
  reducers: {
    // ✅ NEW: Clear dashboard data (useful for logout)
    clearDashboardData: (state) => {
      state.dashboardSummary = null;
      state.quickStats = null;
      state.dashboardLastUpdate = null;
      state.dashboardError = null;
      state.financeRangeStats = null;
      state.financeRangeError = null;
    },
    
    // ✅ NEW: Update quick stats only (for real-time updates)
    updateQuickStatsOnly: (state, action) => {
      state.quickStats = action.payload;
      state.dashboardLastUpdate = Date.now();
    },
    
    // ✅ NEW: Clear errors
    clearDashboardError: (state) => {
      state.dashboardError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchAccountInfo
      .addCase(fetchAccountInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAccountInfo.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchAccountInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // resetPassword
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // updateAccountInfo
      .addCase(updateAccountInfo.fulfilled, (state, action) => {
        state.data = action.payload;
      })

      // fetchUserInfo
      .addCase(fetchUserInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchAccountUsers
      .addCase(fetchAccountUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAccountUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loading = false;
      })
      .addCase(fetchAccountUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ NEW: fetchDashboardSummary
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.dashboardSummary = action.payload;
        state.dashboardLoading = false;
        state.dashboardLastUpdate = Date.now();
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })

      // ✅ NEW: fetchQuickStats
      .addCase(fetchQuickStats.pending, (state) => {
        // Don't set loading for quick stats to avoid UI flicker
        state.dashboardError = null;
      })
      .addCase(fetchQuickStats.fulfilled, (state, action) => {
        state.quickStats = action.payload;
        state.dashboardLastUpdate = Date.now();
      })
      .addCase(fetchQuickStats.rejected, (state, action) => {
        state.dashboardError = action.payload;
      })

      // ✅ NEW: refreshDashboardData
      .addCase(refreshDashboardData.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(refreshDashboardData.fulfilled, (state, action) => {
        state.dashboardSummary = action.payload.summary;
        state.quickStats = action.payload.quickStats;
        state.dashboardLoading = false;
        state.dashboardLastUpdate = action.payload.timestamp;
      })
      .addCase(refreshDashboardData.rejected, (state, action) => {
        state.dashboardLoading = false;
        // Only set error if it's not a "data is fresh" rejection
        if (action.payload !== 'Data is still fresh') {
          state.dashboardError = action.payload;
        }
      })

      // ✅ NEW: fetchOptionalCardData
      .addCase(fetchOptionalCardData.pending, (state, action) => {
        const url = OPTIONAL_CARD_ENDPOINTS[action.meta.arg];
        if (url) state.optionalCardsLoading[url] = true;
      })
      .addCase(fetchOptionalCardData.fulfilled, (state, action) => {
        const { url, data } = action.payload;
        state.optionalCardsData[url] = data;
        state.optionalCardsLoading[url] = false;
      })
      .addCase(fetchOptionalCardData.rejected, (state, action) => {
        const url = OPTIONAL_CARD_ENDPOINTS[action.meta.arg];
        if (url) state.optionalCardsLoading[url] = false;
      })

      // ✅ NEW: fetchFinanceRangeStats
      .addCase(fetchFinanceRangeStats.pending, (state) => {
        state.financeRangeLoading = true;
        state.financeRangeError = null;
      })
      .addCase(fetchFinanceRangeStats.fulfilled, (state, action) => {
        state.financeRangeStats = action.payload;
        state.financeRangeLoading = false;
      })
      .addCase(fetchFinanceRangeStats.rejected, (state, action) => {
        state.financeRangeLoading = false;
        state.financeRangeError = action.payload;
      })

      // ✅ NEW: saveDashboardCardPrefs
      .addCase(saveDashboardCardPrefs.fulfilled, (state, action) => {
        if (state.user) {
          state.user.dashboard_card_prefs = action.payload.dashboard_card_prefs;
        }
      });
  }
});

// ✅ Export actions
export const { clearDashboardData, updateQuickStatsOnly, clearDashboardError } = accountSlice.actions;

// ✅ Export selectors for easy access
export const selectDashboardSummary = (state) => state.account.dashboardSummary;
export const selectQuickStats = (state) => state.account.quickStats;
export const selectDashboardLoading = (state) => state.account.dashboardLoading;
export const selectDashboardError = (state) => state.account.dashboardError;
export const selectDashboardLastUpdate = (state) => state.account.dashboardLastUpdate;

// ✅ Computed selectors
export const selectStudentMetrics = (state) => state.account.dashboardSummary?.student_metrics;
export const selectClassMetrics = (state) => state.account.dashboardSummary?.class_metrics;
export const selectBusMetrics = (state) => state.account.dashboardSummary?.bus_metrics;
export const selectFinancialMetrics = (state) => state.account.dashboardSummary?.financial_metrics;
export const selectDashboardAlerts = (state) => state.account.dashboardSummary?.alerts || [];

// ✅ NEW: Optional card selectors
// null/undefined = user has never customized their cards -> caller should fall back to defaults.
// [] = user explicitly hid every card -> caller must respect that, not fall back.
export const selectDashboardCardPrefs = (state) => state.account.user?.dashboard_card_prefs;
export const selectOptionalCardData = (cardId) => (state) => {
  const url = OPTIONAL_CARD_ENDPOINTS[cardId];
  return url ? state.account.optionalCardsData[url] : undefined;
};
export const selectOptionalCardLoading = (cardId) => (state) => {
  const url = OPTIONAL_CARD_ENDPOINTS[cardId];
  return url ? !!state.account.optionalCardsLoading[url] : false;
};

export default accountSlice.reducer;